import type { InferOutput } from 'valibot'
import defu from 'defu'
import { array, boolean, fallback, intersect, maxLength, maxValue, minValue, nullish, number, object, optional, parse, picklist, pipe, string } from 'valibot'
import { GLOBAL_SEARCH_REQUEST_KINDS, SolrParamsKey } from '~~/shared/solr'
import { MAX_SEARCH_LENGTH, MIN_GLOBAL_SEARCH_LENGTH } from '~/constants/search'
import { baseBodySchema } from '~/utils/solr/body'
import { jsonFacetSearchResponseSchema } from '~/utils/solr/facet'
import { headersSchema } from '~/utils/solr/headers'
import { solrHighlightingSchema } from '~/utils/solr/highlighting'
import { searchResultSchema } from '~/utils/solr/search'
import { spellcheckResponseSchema } from '~/utils/solr/spellcheck'

export const solrResponseSchema = object({
  responseHeader: headersSchema,
  response: intersect([
    baseBodySchema,
    object({
      docs: safeArray(searchResultSchema),
    }),
  ]),
  highlighting: nullish(solrHighlightingSchema),
  facets: nullish(jsonFacetSearchResponseSchema),
  spellcheck: fallback(nullish(spellcheckResponseSchema), null),
  searchDiagnostics: nullish(object({
    transportRetried: nullish(boolean()),
  })),
})

export type SolrResponseSchema = InferOutput<typeof solrResponseSchema>

export const searchFiltersSchema = object({
  contentTypes: nullish(array(string())),
  legalAreas: nullish(array(string())),
  startYear: nullish(string()),
  endYear: nullish(string()),
  authors: nullish(array(string())),
})

export type SearchFilters = InferOutput<typeof searchFiltersSchema>

const searchParamsSchema = object({
  requestKind: optional(picklist(GLOBAL_SEARCH_REQUEST_KINDS)),
  rows: pipe(number(), minValue(1), maxValue(15)),
  start: number(),
  q: pipe(string(), maxLength(MAX_SEARCH_LENGTH)),
  filters: nullish(searchFiltersSchema),
  sort: nullish(string()),
})

export type SearchParams = InferOutput<typeof searchParamsSchema>

export const globalSearchParamsSchema = object({
  [SolrParamsKey.GlobalSearch]: nullish(searchParamsSchema),
})

export type GlobalSearchParams = InferOutput<typeof globalSearchParamsSchema>

export function useSearchQueryOptions(params: MaybeRef<GlobalSearchParams>) {
  const fetch = useRequestFetch()

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- fetch is useRequestFetch() (stable per-render), not a real dep
  return queryOptions({
    queryKey: computed(() => ['global-search', {
      q: unref(params)?.[SolrParamsKey.GlobalSearch]?.q,
      filters: unref(params)?.[SolrParamsKey.GlobalSearch]?.filters,
      sort: unref(params)?.[SolrParamsKey.GlobalSearch]?.sort,
      page: unref(params)?.[SolrParamsKey.GlobalSearch]?.start,
      requestKind: unref(params)?.[SolrParamsKey.GlobalSearch]?.requestKind,
    }]),
    enabled: computed(() => {
      const q = unref(params)?.[SolrParamsKey.GlobalSearch]?.q
      return !!q && q.length >= MIN_GLOBAL_SEARCH_LENGTH && q.length <= MAX_SEARCH_LENGTH
    }),
    async queryFn({ signal }) {
      const res = await fetch('/api/solr/search', {
        params: unref(params),
        signal,
      })

      return parse(solrResponseSchema, res)
    },
  })
}

type QueryOptions = ReturnType<typeof useSearchQueryOptions>

export function useSolrSearchQuery(params: MaybeRef<GlobalSearchParams>, options?: Partial<QueryOptions>) {
  const initialOptions = useSearchQueryOptions(params)
  const resolvedOptions = computed(() => defu<QueryOptions, [QueryOptions]>(options, initialOptions))

  return useQuery(resolvedOptions.value)
}
